import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, setDoc } from "firebase/firestore";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  Select as MUISelect,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Autocomplete from '@mui/material/Autocomplete';

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hours = Array.from({ length: 19 }, (_, i) => i + 5); // 5am to 11pm

const timeZoneOptions = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "America/New_York", label: "America/New_York (EST)" },
  { value: "Europe/London", label: "Europe/London (GMT)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST)" },
  // Add more as needed
];

const countryOptions = [
  { value: "IN", label: "India" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "CN", label: "China" },
  { value: "JP", label: "Japan" },
  { value: "DE", label: "Germany" },
  // Add more as needed
];

export default function AvailabilityForm({ profile, onSaved }) {
  const [availability, setAvailability] = useState(() => {
    const initial = profile?.availability || {};
    return Object.fromEntries(
      days.map(day => [day, Array.isArray(initial[day]) ? initial[day] : []])
    );
  });
  const [timeZone, setTimeZone] = useState(profile?.timeZone || "");
  const [location, setLocation] = useState(profile?.location || "");
  const [country, setCountry] = useState(profile?.country || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    const initial = profile?.availability || {};
    setAvailability(
      Object.fromEntries(
        days.map(day => [day, Array.isArray(initial[day]) ? initial[day] : []])
      )
    );
    setTimeZone(profile?.timeZone || "");
    setLocation(profile?.location || "");
    setCountry(profile?.country || "");
  }, [profile]);

  const handleAvailChange = (day, hour) => {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day]?.includes(hour)
        ? prev[day].filter(h => h !== hour)
        : [...(prev[day] || []), hour].sort((a, b) => a - b)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) return;
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        availability: Object.fromEntries(
          Object.entries(availability).map(([day, hoursArr]) => [day, hoursArr.map(Number)])
        ),
        timeZone,
        location,
        country,
      }, { merge: true });
      setMessage("Saved!");
      setShowSnackbar(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError("Error saving availability");
      setShowSnackbar(true);
      console.error("Error saving availability:", err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setMessage("");
        setError("");
        setShowSnackbar(false);
      }, 2500);
    }
  };

  return (
    <Card sx={{ maxWidth: 700, margin: '32px auto', borderRadius: 3, boxShadow: 3 }}>
      <CardHeader
        avatar={<AccessTimeIcon color="primary" sx={{ fontSize: 40 }} />}
        title={<Typography variant="h5" align="center">My Availability</Typography>}
      />
      <CardContent>
        <form onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="tz-label">Time Zone</InputLabel>
                <MUISelect
                  labelId="tz-label"
                  value={timeZone}
                  label="Time Zone"
                  onChange={e => setTimeZone(e.target.value)}
                >
                  {timeZoneOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </MUISelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="country-label">Country</InputLabel>
                <MUISelect
                  labelId="country-label"
                  value={country}
                  label="Country"
                  onChange={e => setCountry(e.target.value)}
                >
                  {countryOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </MUISelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Location (City/Area)"
                value={location}
                onChange={e => setLocation(e.target.value)}
                fullWidth
                placeholder="Enter city or area"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Availability (5am-11pm):</b></Typography>
              <Grid container spacing={1}>
                {days.map(day => (
                  <Grid item xs={12} sm={6} md={3} key={day}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{day}</Typography>
                    <Grid container spacing={0.5}>
                      {hours.map(hour => (
                        <Grid item key={hour}>
                          <Button
                            variant={availability[day]?.includes(hour) ? "contained" : "outlined"}
                            color={availability[day]?.includes(hour) ? "primary" : "inherit"}
                            size="small"
                            onClick={() => handleAvailChange(day, hour)}
                            sx={{ minWidth: 40, mb: 0.5 }}
                          >
                            {hour}:00
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
                sx={{ mt: 2 }}
              >
                {loading ? "Saving..." : "Save Availability"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
      <Snackbar open={showSnackbar} autoHideDuration={2500} onClose={() => setShowSnackbar(false)}>
        {message ? (
          <Alert severity="success" sx={{ width: '100%' }}>{message}</Alert>
        ) : error ? (
          <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
        ) : null}
      </Snackbar>
    </Card>
  );
}
